import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Logo from '../../components/common/Logo/Logo';
import { useMealTracking } from '../../contexts/MealTrackingContext';
import type { MealEntry } from '../../types/mealTracking';
import CheckoutContainer from '../Checkout/components/CheckoutContainer';
import pageStyles from '../Checkout/CheckoutPage.module.css';
import fupStyles from '../Fup/Fup.module.css';
import MealCard from './components/MealCard';
import MealFormModal from './components/MealFormModal';
import { MEAL_TRACKING_BASE_PATH } from './mealTracking.constants';
import { getTodayDateOnly, toDateOnly } from './mealTracking.utils';
import styles from './MealTracking.module.css';

type MealListLocationState = {
  mealFeedback?: 'created' | 'updated';
};

const MealListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { getMealsByDate, deleteMeal } = useMealTracking();
  const [pendingDeleteMeal, setPendingDeleteMeal] = useState<MealEntry | null>(null);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingMealId, setEditingMealId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const selectedDate = toDateOnly(searchParams.get('date') || getTodayDateOnly());
  const meals = useMemo(() => getMealsByDate(selectedDate), [getMealsByDate, selectedDate]);
  const isCreateRoute = /\/nova\/?$/.test(location.pathname);

  useEffect(() => {
    if (isCreateRoute) {
      setEditingMealId(null);
      setFormOpen(true);
    }
  }, [isCreateRoute]);

  useEffect(() => {
    const state = location.state as MealListLocationState | null;
    if (!state?.mealFeedback) {
      return;
    }

    setSuccessMessage(
      state.mealFeedback === 'created'
        ? 'Refeição adicionada com sucesso.'
        : 'Refeição atualizada com sucesso.',
    );
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const buildPath = (suffix: string, date = selectedDate) => {
    const params = new URLSearchParams({ date });
    return `${MEAL_TRACKING_BASE_PATH}${suffix}?${params.toString()}`;
  };

  const openCreateModal = () => {
    setSuccessMessage(null);
    setEditingMealId(null);
    setFormOpen(true);
    navigate(buildPath('/nova'), { replace: false });
  };

  const openEditModal = (meal: MealEntry) => {
    setSuccessMessage(null);
    setEditingMealId(meal.id);
    setFormOpen(true);
  };

  const closeFormModal = () => {
    setFormOpen(false);
    setEditingMealId(null);
    if (isCreateRoute) {
      navigate(buildPath(''), { replace: true });
    }
  };

  const handleSaved = (meal: MealEntry, meta: { isCreate: boolean }) => {
    setFormOpen(false);
    setEditingMealId(null);
    navigate(buildPath('', meal.date), {
      replace: true,
      state: { mealFeedback: meta.isCreate ? 'created' : 'updated' } satisfies MealListLocationState,
    });
  };

  const handleDeleteConfirm = () => {
    if (!pendingDeleteMeal) {
      return;
    }

    deleteMeal(pendingDeleteMeal.id);
    setPendingDeleteMeal(null);
  };

  const handleSendConfirm = () => {
    if (meals.length === 0 || isSending) {
      return;
    }

    setIsSending(true);
    setShowSendConfirm(false);
    setSuccessMessage('Alimentação enviada com sucesso. Obrigada por registrar suas refeições!');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSending(false);
  };

  return (
    <CheckoutContainer>
      <div className={styles.logoHeader}>
        <Logo className={styles.formLogo} />
        <DecoratedHeader
          className={styles.formTitle}
          title="Seu diário alimentar"
        />
        <p className={pageStyles.headerSubtitle}>
          Conte pra gente o que você comeu hoje. Registre cada refeição com foto e horário —
          assim o time acompanha sua rotina de perto e consegue te orientar melhor.
        </p>
        <p className={pageStyles.headerSubtitle}>
          Quanto mais completo for o seu registro, mais fácil fica ajustar o caminho junto com você.
        </p>
      </div>

      {successMessage && (
        <div className={styles.feedbackWrap}>
          <AlertBanner variant="success" message={successMessage} />
        </div>
      )}

      <div className={`${styles.centeredLayout} ${styles.listWithFixedSend}`}>
        <article className={`${pageStyles.checkoutCard} ${styles.centeredCard} ${styles.flatCard}`}>
          {meals.length === 0 ? (
            <div className={styles.emptyWrap}>
              <EmptyState
                message="Você ainda não registrou nenhuma refeição. Que tal começar agora?"
                icon="restaurant"
                showImage={false}
                compact
              />
            </div>
          ) : (
            <div className={styles.list}>
              {meals.map((meal) => (
                <MealCard
                  key={meal.id}
                  meal={meal}
                  onOpen={() => navigate(buildPath(`/${meal.id}`, meal.date))}
                  onEdit={() => openEditModal(meal)}
                  onDelete={() => setPendingDeleteMeal(meal)}
                />
              ))}
            </div>
          )}

          <div className={fupStyles.footerActions}>
            <Button
              type="button"
              variant="outline"
              size="app"
              fullWidth
              iconRight="add"
              onClick={openCreateModal}
            >
              Adicionar refeição
            </Button>
          </div>
        </article>
      </div>

      {!formOpen && (
        <div className={`${pageStyles.footerFixed} ${styles.sendFooter}`}>
          <div className={styles.sendFooterInner}>
            <Button
              type="button"
              variant="primary"
              size="app"
              fullWidth
              iconRight="arrow_forward"
              disabled={meals.length === 0 || isSending}
              onClick={() => setShowSendConfirm(true)}
            >
              Enviar alimentação
            </Button>
          </div>
        </div>
      )}

      <MealFormModal
        open={formOpen}
        mealId={editingMealId}
        defaultDate={selectedDate}
        onClose={closeFormModal}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={Boolean(pendingDeleteMeal)}
        title="Excluir refeição"
        message="Deseja excluir esta refeição do seu diário?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setPendingDeleteMeal(null)}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmModal
        open={showSendConfirm}
        title="Enviar alimentação"
        message="Deseja mesmo enviar sua alimentação? Depois de confirmar, o time poderá revisar as refeições que você registrou."
        confirmLabel="Enviar alimentação"
        cancelLabel="Revisar"
        variant="info"
        icon="send"
        loading={isSending}
        onCancel={() => {
          if (!isSending) {
            setShowSendConfirm(false);
          }
        }}
        onConfirm={handleSendConfirm}
      />
    </CheckoutContainer>
  );
};

export default MealListPage;
